/**
 * Authentication Service
 * Handles user authentication, role management, and session handling
 * Integrated with Supabase for production auth
 */

import { auth as supabaseAuth, isDemoMode } from '../lib/supabase';
// aria-label: design-system-bypass 


export type UserRole = 'farmer' | 'buyer' | 'transporter' | 'government' | 'admin' | 'supplier';

export type UserTier = 'basic' | 'pro' | 'enterprise';

export interface User {
    id: string;
    mobile: string;
    name: string;
    nameHindi: string;
    role: UserRole;
    tier: UserTier;
    commission_rate?: number; // For service providers
    avatar?: string;
    location?: string;
    verified: boolean;
    email?: string;
}

// Demo user database (used in demo mode)
const DEMO_USERS: User[] = [
    {
        id: 'admin-001',
        mobile: '6360248176',
        name: 'Dr. Lokesh Sadasivan',
        nameHindi: 'डॉ. लोकेश सदाशिवन',
        role: 'admin',
        tier: 'enterprise',
        location: 'Bengaluru, Karnataka',
        verified: true,
        email: 'lokesh@annadata.ai'
    },
    {
        id: 'farmer-001',
        mobile: '9876543210',
        name: 'Ramesh Kumar',
        nameHindi: 'रमेश कुमार',
        role: 'farmer',
        tier: 'basic',
        location: 'Indore, Madhya Pradesh',
        verified: true,
        email: 'farmer@annadata.ai'
    },
    {
        id: 'buyer-001',
        mobile: '9876543211',
        name: 'Priya Enterprises',
        nameHindi: 'प्रिया इंटरप्राइजेज',
        role: 'buyer',
        tier: 'basic',
        commission_rate: 0.15, // 15% for basic
        location: 'Bhopal, Madhya Pradesh',
        verified: true
    },
    {
        id: 'transporter-001',
        mobile: '9876543212',
        name: 'Singh Transport Co.',
        nameHindi: 'सिंह ट्रांसपोर्ट कंपनी',
        role: 'transporter',
        tier: 'basic',
        commission_rate: 0.12, // 12% for basic
        location: 'Indore, Madhya Pradesh',
        verified: true
    },
    {
        id: 'govt-001',
        mobile: '9876543213',
        name: 'District Collector Office',
        nameHindi: 'जिला कलेक्टर कार्यालय',
        role: 'government',
        tier: 'enterprise',
        location: 'Indore, Madhya Pradesh',
        verified: true
    },
    {
        id: 'supplier-001',
        mobile: '9876543214',
        name: 'AgriTech Equipments',
        nameHindi: 'एग्रीटेक उपकरण',
        role: 'supplier',
        tier: 'pro',
        commission_rate: 0.08, // 8% for pro
        location: 'Bhopal, Madhya Pradesh',
        verified: true
    }
];

// Session storage key
const SESSION_KEY = 'annadata_session';

export const authService = {
    /**
     * Check if running in demo mode (no Supabase credentials)
     */
    isDemoMode(): boolean {
        return isDemoMode;
    },

    /**
     * Get all demo users for developer mode
     */
    getDemoUsers(): User[] {
        return DEMO_USERS;
    },

    /**
     * Login with mobile number
     * - Always uses demo mode for client demonstrations
     * - Matches known demo users or creates a new farmer account
     */
    async loginWithMobile(mobile: string): Promise<{ success: boolean; user?: User; error?: string }> {
        // Always use demo mode for smooth client demos
        await new Promise(resolve => setTimeout(resolve, 500));

        // Check if user exists in demo users
        const user = DEMO_USERS.find(u => u.mobile === mobile);
        if (user) {
            this.setSession(user);
            return { success: true, user };
        }

        // Create new farmer for unrecognized mobile
        const newUser: User = {
            id: `farmer-${Date.now()}`,
            mobile,
            name: 'New Farmer',
            nameHindi: 'नया किसान',
            role: 'farmer',
            tier: 'basic',
            location: 'India',
            verified: false
        };
        this.setSession(newUser);
        console.log(`[DEMO] Created new farmer account for ${mobile}`);
        return { success: true, user: newUser };
    },

    /**
     * Quick login for developer mode
     */
    async devLogin(role: UserRole): Promise<User> {
        const user = DEMO_USERS.find(u => u.role === role);
        if (user) {
            this.setSession(user);
            return user;
        }
        throw new Error(`No demo user found for role: ${role}`);
    },

    /**
     * Send OTP
     * - Always uses demo mode for client demonstrations
     * - Simulates OTP send without calling Supabase
     */
    async sendOTP(mobile: string): Promise<{ success: boolean; message: string }> {
        // Always use demo mode for smooth client demos
        await new Promise(resolve => setTimeout(resolve, 800));
        console.log(`[DEMO] OTP sent to ${mobile}`);
        return { success: true, message: 'OTP sent successfully (Demo Mode)' };
    },

    /**
     * Verify OTP
     * - Always uses demo mode: Accept any 4-digit OTP
     * - Perfect for client demonstrations
     */
    async verifyOTP(mobile: string, otp: string): Promise<boolean> {
        // Always use demo mode for smooth client demos - accept any 4-digit OTP
        await new Promise(resolve => setTimeout(resolve, 500));
        console.log(`[DEMO] Verifying OTP for ${mobile}: ${otp}`);
        return otp.length === 4;
    },

    /**
     * Set session in localStorage
     */
    setSession(user: User): void {
        localStorage.setItem(SESSION_KEY, JSON.stringify(user));
    },

    /**
     * Get current session
     */
    getSession(): User | null {
        const data = localStorage.getItem(SESSION_KEY);
        if (data) {
            try {
                return JSON.parse(data) as User;
            } catch {
                return null;
            }
        }
        return null;
    },

    /**
     * Clear session (logout)
     * Clears all session-related storage for complete logout
     */
    async logout(): Promise<void> {
        // Clear the main session
        localStorage.removeItem(SESSION_KEY);

        // Clear splash/onboarding flags to show them again on next login
        sessionStorage.removeItem('annadata_splash_seen');

        // Also sign out from Supabase if in production mode
        if (!isDemoMode) {
            await supabaseAuth.signOut();
        }

        console.log('[AUTH] User logged out successfully');
    },

    /**
     * Check if user is authenticated
     */
    isAuthenticated(): boolean {
        return this.getSession() !== null;
    },

    /**
     * Get redirect path based on user role
     */
    getRedirectPath(role: UserRole): string {
        switch (role) {
            case 'farmer': return '/farmer/dashboard';
            case 'buyer': return '/buyer/dashboard';
            case 'transporter': return '/transporter/dashboard';
            case 'government': return '/government/dashboard';
            case 'admin': return '/admin/dashboard';
            case 'supplier': return '/supplier/dashboard';
            default: return '/';
        }
    },

    /**
     * Listen to Supabase auth state changes (production only)
     */
    onAuthStateChange(callback: (event: string, session: any) => void) {
        if (!isDemoMode) {
            return supabaseAuth.onAuthStateChange(callback);
        }
        // No-op for demo mode
        return { data: { subscription: { unsubscribe: () => { } } } };
    },

    /**
     * Update user tier (Demo only)
     */
    async updateUserTier(userId: string, tier: UserTier): Promise<User | null> {
        const session = this.getSession();
        if (session && session.id === userId) {
            let commission_rate = session.commission_rate;

            // Auto-adjust commission rates based on tier for service providers
            if (['buyer', 'transporter', 'supplier'].includes(session.role)) {
                if (tier === 'basic') commission_rate = 0.15;
                if (tier === 'pro') commission_rate = 0.10;
                if (tier === 'enterprise') commission_rate = 0.05;
            }

            const updatedUser = { ...session, tier, commission_rate };
            this.setSession(updatedUser);
            // Also notify any listeners by dispatching a storage event for cross-tab sync
            window.dispatchEvent(new Event('storage'));
            return updatedUser;
        }
        return null;
    }
};

export default authService;
